// ────────────────────────────────────────────────────────────────────────────
// Transfer Organiser algorithm (b15a / v1.5.0).
//
// Premium one-time feature ($8.99, RevenueCat entitlement transferOrganiser).
// Takes the trainer's scanned dex and produces an optimal "what to transfer to
// Professor Willow" list. All math is LOCAL — the dex is already on device, so
// there is no server round-trip and no Pokémon GO API dependency (PoGo has no
// public transfer API). This is a RECOMMENDATION tool, not an executor.
//
// Design constraints from the brief:
//   - Per unique species, keep the top N highest-scoring instances.
//   - Always keep: shinies, luckies, XXS, perfect (100%), nundo (0%, PvP), plus
//     every variant form (shadow/purified counted as forms here) — each subject
//     to its own preservation toggle so the trainer stays in control.
//   - Evolution-chain protection: keep >=1 instance of a species even if all of
//     its instances scored low, so a future evolution is never orphaned. (v1
//     interprets "chain" conservatively as "never empty a species" — full chain
//     graphs are a later iteration.)
//   - Transfer candidates are ordered smallest-stardust-loss first (lowest CP
//     first within a species) so the trainer can stop at any point and the most
//     valuable duplicates are always the last to go.
// ────────────────────────────────────────────────────────────────────────────

import type { InventoryRecord } from "./inventory";

/** Stardust returned by Professor Willow for a single transfer. Flat 100 for a
 *  normal mon (Pokémon GO has paid this since the dust-on-transfer change). We
 *  keep it a named constant so the summary math reads clearly and a future
 *  per-rarity table can slot in without touching call sites. */
export const STARDUST_PER_TRANSFER = 100;

/** User-tunable preservation + keep settings. Mirrors the `user_transfer_settings`
 *  D1 row (snake_case there, camelCase here). Synced across devices. */
export interface TransferSettings {
  /** Keep the top-N highest-scoring instances per species (1–10). */
  keepTopN: number;
  preserveShiny: boolean;
  preserveLucky: boolean;
  preserveXxs: boolean;
  preservePerfect: boolean;
  preserveNundo: boolean;
  /** Keep at least one instance of every species even if all score low. */
  evolutionProtect: boolean;
}

export const DEFAULT_TRANSFER_SETTINGS: TransferSettings = {
  keepTopN: 3,
  preserveShiny: true,
  preserveLucky: true,
  preserveXxs: true,
  preservePerfect: true,
  preserveNundo: true,
  evolutionProtect: true,
};

/** Per-instance scoring bonuses (brief's v1 formula). */
const SCORE_BONUS = {
  shiny: 1000,
  lucky: 500,
  xxs: 300,
  perfect: 800, // 100% IV
  nundo: 600, // 0% IV — PvP "nundo"
} as const;

export interface TransferCandidate {
  id: string;
  species: string;
  dexNumber: number;
  cp: number;
  ivPercent: number; // 0–100
  isShiny: boolean;
  isLucky: boolean;
  isXxs: boolean;
  isPerfect: boolean;
  isNundo: boolean;
  score: number;
  /** Human-readable justification, e.g. "Pidgey, 12% IV, no shiny/lucky — safe transfer". */
  reasoning: string;
  stardustReturn: number;
}

export interface SpeciesGroup {
  dexNumber: number;
  species: string;
  kept: number;
  candidates: TransferCandidate[]; // ordered smallest-stardust-loss first (lowest CP first)
}

export interface TransferPlan {
  totalScanned: number;
  totalCandidates: number;
  totalStardust: number;
  groups: SpeciesGroup[]; // ordered by candidate count desc, then dexNumber asc
}

/** IV percent for a record. Records with unknown IVs (all zero and never
 *  scanned) are treated as 0% so the math stays defined; the OCR pipeline fills
 *  these where it can. */
export function ivPercent(rec: Pick<InventoryRecord, "ivAtk" | "ivDef" | "ivSta">): number {
  const total = (rec.ivAtk ?? 0) + (rec.ivDef ?? 0) + (rec.ivSta ?? 0);
  return (total / 45) * 100;
}

function isXxs(rec: InventoryRecord): boolean {
  return rec.sizeClass === "XXS";
}

function isPerfect(rec: InventoryRecord): boolean {
  return rec.isHundo || (rec.ivAtk === 15 && rec.ivDef === 15 && rec.ivSta === 15);
}

function isNundo(rec: InventoryRecord): boolean {
  return rec.ivAtk === 0 && rec.ivDef === 0 && rec.ivSta === 0;
}

/** Composite score. Higher = keep. See brief's v1 formula. */
export function scoreInstance(rec: InventoryRecord): number {
  return (
    ivPercent(rec) +
    (rec.isShiny ? SCORE_BONUS.shiny : 0) +
    (rec.isLucky ? SCORE_BONUS.lucky : 0) +
    (isXxs(rec) ? SCORE_BONUS.xxs : 0) +
    (isPerfect(rec) ? SCORE_BONUS.perfect : 0) +
    (isNundo(rec) ? SCORE_BONUS.nundo : 0) +
    rec.cp / 100
  );
}

/** True when a record must always be kept because of an active preservation
 *  toggle, independent of its rank within the species. */
function isProtectedByFlag(rec: InventoryRecord, s: TransferSettings): boolean {
  if (s.preserveShiny && rec.isShiny) return true;
  if (s.preserveLucky && rec.isLucky) return true;
  if (s.preserveXxs && isXxs(rec)) return true;
  if (s.preservePerfect && isPerfect(rec)) return true;
  if (s.preserveNundo && isNundo(rec)) return true;
  return false;
}

function buildReasoning(rec: InventoryRecord): string {
  const tags: string[] = [];
  if (rec.isShiny) tags.push("shiny");
  if (rec.isLucky) tags.push("lucky");
  if (isXxs(rec)) tags.push("XXS");
  if (isPerfect(rec)) tags.push("100%");
  if (isNundo(rec)) tags.push("nundo");
  const iv = `${ivPercent(rec).toFixed(0)}% IV`;
  if (tags.length === 0) {
    return `${rec.species}, ${iv}. No shiny/lucky, safe transfer`;
  }
  // A flagged record only reaches the candidate list when its preservation
  // toggle is off; spell out which flag the trainer chose not to protect.
  return `${rec.species}, ${iv}, has ${tags.join("/")} (preservation off). Review before transfer`;
}

/**
 * Build the full transfer plan from the trainer's box records and settings.
 * Pure — no I/O, deterministic for a given input. Excludes legacy CSV records;
 * callers pass the canonical box inventory.
 */
export function buildTransferPlan(
  records: InventoryRecord[],
  settings: TransferSettings,
): TransferPlan {
  const keepN = Math.max(1, Math.min(10, Math.round(settings.keepTopN)));

  // Bucket by species (dexNumber is the stable key; species name is for display).
  const bySpecies = new Map<number, InventoryRecord[]>();
  for (const rec of records) {
    const arr = bySpecies.get(rec.dexNumber);
    if (arr) arr.push(rec);
    else bySpecies.set(rec.dexNumber, [rec]);
  }

  const groups: SpeciesGroup[] = [];

  for (const [dexNumber, instances] of Array.from(bySpecies.entries())) {
    // Rank by score desc; ties broken by CP desc so the bigger mon ranks higher.
    const ranked = [...instances].sort((a, b) => {
      const ds = scoreInstance(b) - scoreInstance(a);
      return ds !== 0 ? ds : b.cp - a.cp;
    });

    const keptIds = new Set<string>();
    // 1. Keep the top-N by score.
    for (let i = 0; i < Math.min(keepN, ranked.length); i += 1) {
      keptIds.add(ranked[i].id);
    }
    // 2. Always keep flag-protected instances regardless of rank.
    for (const rec of ranked) {
      if (isProtectedByFlag(rec, settings)) keptIds.add(rec.id);
    }

    let candidates = ranked.filter((r) => !keptIds.has(r.id));

    // 3. Evolution-chain protection: never empty a species. If every instance
    //    would transfer, retain the single highest-scoring one so a future
    //    evolution always has a base to work from.
    if (settings.evolutionProtect && keptIds.size === 0 && candidates.length > 0) {
      const retain = candidates[0]; // ranked desc, so [0] is the best remaining
      keptIds.add(retain.id);
      candidates = candidates.filter((r) => r.id !== retain.id);
    }

    if (candidates.length === 0) continue;

    // Order candidates smallest-stardust-loss first → lowest CP first, so the
    // trainer can stop transferring at any point and the most valuable
    // duplicates are the last to go.
    candidates.sort((a, b) => a.cp - b.cp);

    groups.push({
      dexNumber,
      species: instances[0].species,
      kept: keptIds.size,
      candidates: candidates.map((rec) => ({
        id: rec.id,
        species: rec.species,
        dexNumber: rec.dexNumber,
        cp: rec.cp,
        ivPercent: ivPercent(rec),
        isShiny: rec.isShiny,
        isLucky: rec.isLucky,
        isXxs: isXxs(rec),
        isPerfect: isPerfect(rec),
        isNundo: isNundo(rec),
        score: scoreInstance(rec),
        reasoning: buildReasoning(rec),
        stardustReturn: STARDUST_PER_TRANSFER,
      })),
    });
  }

  // Surface the biggest wins first: most candidates, then by dex order.
  groups.sort((a, b) => {
    const dc = b.candidates.length - a.candidates.length;
    return dc !== 0 ? dc : a.dexNumber - b.dexNumber;
  });

  const totalCandidates = groups.reduce((n, g) => n + g.candidates.length, 0);

  return {
    totalScanned: records.length,
    totalCandidates,
    totalStardust: totalCandidates * STARDUST_PER_TRANSFER,
    groups,
  };
}

/**
 * Render the plan (minus any candidate ids the trainer has skipped) as a
 * markdown checklist the trainer references while manually transferring in
 * Pokémon GO. Grouped by species, smallest-loss first.
 */
export function exportTransferChecklist(
  plan: TransferPlan,
  skipped: ReadonlySet<string> = new Set(),
): string {
  const lines: string[] = [];
  lines.push("# Transfer list, Nexus Dex");
  lines.push("");
  let count = 0;
  let dust = 0;
  const body: string[] = [];
  for (const group of plan.groups) {
    const items = group.candidates.filter((c) => !skipped.has(c.id));
    if (items.length === 0) continue;
    body.push(`## ${group.species} (${items.length})`);
    for (const c of items) {
      count += 1;
      dust += c.stardustReturn;
      body.push(`- [ ] CP ${c.cp} · ${c.ivPercent.toFixed(0)}% IV, ${c.reasoning}`);
    }
    body.push("");
  }
  lines.push(`${count} Pokémon · ${dust.toLocaleString()} stardust returned`);
  lines.push("");
  lines.push(...body);
  return lines.join("\n").trimEnd() + "\n";
}
